import * as Iterable_ from 'effect/Iterable';
import { Nullable } from '../Nullable/index.js';
import { identity } from '../function/index.js';
//#region Constructors
/**
 * Generates iterator going through range inclusively
 */
export function* range(from, to) {
    for (let i = from; i <= to; i++) {
        yield i;
    }
}
//#endregion
//#region Combinators
export const compact = (iterable) => Iterable_.filter(Nullable.isNonNullable)(iterable);
export const windowCount = (count) => {
    return function* (iterable) {
        let buffer = [];
        for (const item of iterable) {
            if (buffer.length === count) {
                yield buffer;
                buffer = [];
            }
            else {
                buffer.push(item);
            }
        }
        if (buffer.length > 0) {
            yield buffer;
        }
    };
};
export const uniqBy = (getProperty) => function* (iterable) {
    const set = new Set();
    for (const item of iterable) {
        if (set.has(getProperty(item))) {
            continue;
        }
        yield item;
        set.add(getProperty(item));
    }
};
export const uniq = uniqBy(identity);
export const uniqByConsecutive = (getProperty) => function* (iterable) {
    let lastProperty = undefined;
    let first = true;
    for (const item of iterable) {
        const currentProperty = getProperty(item);
        if (!first && currentProperty === lastProperty) {
            continue;
        }
        yield item;
        first = false;
        lastProperty = currentProperty;
    }
};
export const uniqConsecutive = uniqByConsecutive(identity);
export const concat = (first) => function* (second) {
    yield* first;
    yield* second;
};
export const intersection = (firstIterable) => {
    const bufferSet = new Set(firstIterable);
    return function* (secondIterable) {
        for (const item of secondIterable) {
            if (bufferSet.has(item)) {
                yield item;
            }
        }
    };
};
/**
 * @example
 * const a = [1,2,3,4,5];
 * pipe(a, dropWhile(value => value > 2))
 * // [3,4,5]
 */
export const dropWhile = (predicate) => function* (iterable) {
    let index = 0;
    let dropDone = false;
    for (const item of iterable) {
        if (!dropDone && predicate(item, index++)) {
            continue;
        }
        dropDone = true;
        yield item;
    }
};
/**
 * @example
 * const a = [1,2,3,4,5];
 * pipe(a, dropWhile(value => value > 3))
 * // [1,2]
 */
export const dropTailWhile = (predicate) => function* (iterable) {
    const buffer = toArray(iterable);
    let dropDone = false;
    let currDropIndex = buffer.length - 1;
    while (!dropDone && currDropIndex >= 0) {
        if (!predicate(buffer[currDropIndex], currDropIndex)) {
            dropDone = true;
            break;
        }
        currDropIndex--;
    }
    let index = 0;
    for (const item of buffer) {
        if (index === currDropIndex + 1) {
            break;
        }
        yield item;
        index++;
    }
};
//#endregion
//#region Folding
export const count = (predicate = () => true) => (iterable) => {
    let count = 0;
    for (const item of iterable) {
        if (predicate(item)) {
            count++;
        }
    }
    return count;
};
/**
 * Same as https://lodash.com/docs/4.17.15#sumBy but for iterables
 * @param getProperty
 * @returns
 */
export const sumBy = (getProperty) => (iterable) => {
    let sum = 0;
    for (const item of iterable) {
        sum += getProperty(item);
    }
    return sum;
};
export const sum = sumBy(identity);
export const slidingWindow = (size) => function* (iterable) {
    const currentWindow = [];
    for (const item of iterable) {
        currentWindow.push(item);
        if (currentWindow.length === size) {
            yield [...currentWindow];
            currentWindow.shift();
        }
    }
};
/**
 * @example
 *
 * const nums = [1,2,3,5,6,8,10]
 * pipe(nums, partition((currentGroup, current) => current - last(currentGroup) === 1);
 * // [[1,2,3], [5,6], [8], [10]]
 */
export const parition = (partitionFn) => (iterable) => {
    const groups = [];
    for (const item of iterable) {
        if (groups.length === 0) {
            groups.push([item]);
        }
        else {
            const lastGroup = groups.at(-1);
            if (partitionFn(lastGroup, item)) {
                lastGroup.push(item);
            }
            else {
                groups.push([item]);
            }
        }
    }
    return groups;
};
//#region Set Operations
/**
 * The difference (subtraction) is defined as follows.
 * The set A−B consists of elements that are in A but not in B. For example if A={1,2,3} and B={3,5}, then A−B={1,2}.
 * @example
 * const A = [1,2,3]
 * const B = [3,5]
 * const C = pipe(A, difference(B), toArray)
 * // C = [1,2]
 */
export const difference = (b) => function* (a) {
    const bufferSet = new Set(b);
    for (const item of a) {
        if (!bufferSet.has(item)) {
            bufferSet.add(item);
            yield item;
        }
    }
};
/**
 * Alias for @see {difference}
 */
export const without = difference;
/**
 * The difference (subtraction) is defined as follows.
 * The set A−B consists of elements that are in A but not in B. For example if A={1,2,3} and B={3,5}, then A−B={1,2}.
 * @example
 * const A = [1,2,3]
 * const B = ['3','5']
 * const C = pipe(A, differenceWith(B, (a, b) => a === Number(b)), toArray)
 * // C = [1,2]
 */
export const differenceWith = (b, comparator) => {
    const bs = [...b];
    return function* (a) {
        for (const item of a) {
            if (!bs.some((bItem) => comparator(item, bItem))) {
                yield item;
            }
        }
    };
};
//#endregion
/**
 * Get minimum item in iterable by property
 * @example
 * type User = {age: number};
 * const getAge = (u: User) => u.age;
 * const getYoungestUser = maxBy(getAge);
 *
 * const youngestUser: User = getYoungestUser([{age: 3}, ...])
 */
export const minBy = (getProperty) => (iterable) => {
    let currentMin = undefined;
    let minItem = undefined;
    for (const item of iterable) {
        const current = getProperty(item);
        if (currentMin === undefined || current < currentMin) {
            currentMin = current;
            minItem = item;
        }
    }
    return minItem;
};
export const min = (iterable) => minBy(identity)(iterable);
/**
 * Get maximum item in iterable by property
 * @example
 * type User = {age: number};
 * const getAge = (u: User) => u.age;
 * const getOldestUser = maxBy(getAge);
 *
 * const oldestUser: User = getOldestUser([{age: 3}, ...])
 */
export const maxBy = (getProperty) => (iterable) => {
    let currentMax = undefined;
    let maxItem = undefined;
    for (const item of iterable) {
        const current = getProperty(item);
        if (currentMax === undefined || current > currentMax) {
            currentMax = current;
            maxItem = item;
        }
    }
    return maxItem;
};
export const max = (iterable) => maxBy(identity)(iterable);
/**
 * Computes the average of a sequence of numeric values.
 */
export const averageBy = (getProperty) => (iterable) => {
    let count = 0;
    let sum = 0;
    for (const item of iterable) {
        const current = getProperty(item);
        count++;
        sum = sum === undefined ? current : sum + current;
    }
    return sum === undefined ? undefined : sum / count;
};
export const average = (iterable) => averageBy(identity)(iterable);
export const find = (predicate) => (iterable) => {
    for (const item of iterable) {
        if (predicate(item)) {
            return item;
        }
    }
};
/**
 * Iterates collection to Record
 * Unlike groupWith each key contains single element, if there is a collision first found element will be used
 * @example
 *
 * type User {id: string; name: string}
 * const getId = (u: User) => u.id;
 * const getName = (u: User) => u.name;
 *
 * const createUserByIdRecord = toRecord(getId)
 * const createUserNameByIdRecord = toRecord(getId, getName);
 *
 * const usersById = createUserByIdRecord(usersArray);
 * const userNamesById = createUserNameByIdRecord(usersArray);
 *
 */
export const toRecord = (getKey, getValue) => (iterable) => {
    const result = {};
    for (const item of iterable) {
        const key = getKey(item);
        if (!result[key]) {
            result[key] = (getValue ? getValue(item) : item);
        }
    }
    return result;
};
export const toArray = (iterable) => {
    const array = [...iterable];
    return array.length === 0 ? [] : array;
};
export const toSet = (iterable) => new Set(iterable);
export const toMap = (getKey) => (iterable) => {
    const map = new Map();
    for (const item of iterable) {
        const key = getKey(item);
        map.set(key, item);
    }
    return map;
};
export const join = (separator = ',') => (iterable) => {
    let result = '';
    for (const item of iterable) {
        result += item + separator;
    }
    return result;
};
//#endregion
export * from 'effect/Iterable';
//# sourceMappingURL=Iterable.js.map